import type { Campaign, CampaignPatch } from "../../lib/types";
import { contrast, isHexColor, legibleOn } from "./color";

export type AppearanceKey =
	"accentColor" | "widgetBackground" | "widgetText" | "popupBackground" | "popupText";

/** A colour per key; null follows the shop's theme. */
export type Appearance = Record<AppearanceKey, string | null>;

export const appearanceOf = (source: Campaign | CampaignPatch): Appearance => ({
	accentColor: source.accentColor ?? null,
	widgetBackground: source.widgetBackground ?? null,
	widgetText: source.widgetText ?? null,
	popupBackground: source.popupBackground ?? null,
	popupText: source.popupText ?? null,
});

export const isColorValid = (value: string | null): boolean => value === null || isHexColor(value);

/** Every colour is whole, so the form can refuse to save one half typed. */
export const isAppearanceValid = (appearance: Appearance): boolean =>
	Object.values(appearance).every(isColorValid);

/**
 * Where a colour starts when the merchant first chooses one: the storefront's
 * own fallbacks, so turning a colour on does not jump the preview.
 */
export const STARTING_COLORS: Record<AppearanceKey, string> = {
	accentColor: "#111111",
	widgetBackground: "#ffffff",
	widgetText: "#111111",
	popupBackground: "#ffffff",
	popupText: "#111111",
};

/**
 * What the preview stands in for a theme it cannot see: the storefront's own
 * fallbacks when nothing paints behind the widget. The admin's colours would be
 * wrong in dark mode, where no storefront default is dark.
 */
const THEME_STAND_IN = { background: "#ffffff", text: "#111111" } as const;

/** The storefront's thresholds, in app-shopify/storefront/src/accent.ts. */
const MIN_TEXT_CONTRAST = 4.5;
const MIN_ACCENT_CONTRAST = 1.6;

export type Surface = {
	background: string;
	text: string;
	accent: string;
	accentInk: string;
};

type Behind = { background: string; text: string };

/**
 * The colours the storefront paints a surface with, by the same rules as its
 * `dialogColors` and `applyAccent`: whatever was not chosen gives way when the
 * pair cannot be read, a pair the merchant chose both of is left alone, and an
 * accent that would vanish on its background gives way to the text.
 */
const resolve = (
	behind: Behind,
	background: string | null,
	text: string | null,
	accent: string | null,
): Surface => {
	let surface = background ?? behind.background;
	let ink = text ?? behind.text;

	if (contrast(surface, ink) < MIN_TEXT_CONTRAST) {
		if (text && !background) surface = legibleOn(ink);
		else if (!(text && background)) ink = legibleOn(surface);
	}

	const visible = accent && contrast(accent, surface) >= MIN_ACCENT_CONTRAST ? accent : ink;
	return { background: surface, text: ink, accent: visible, accentInk: legibleOn(visible) };
};

/**
 * The popup takes its unchosen colours from the progress bar card, because the
 * storefront matches the dialog to the widget the shopper was just looking at.
 */
export const resolveAppearance = (appearance: Appearance): { widget: Surface; popup: Surface } => {
	const widget = resolve(
		THEME_STAND_IN,
		appearance.widgetBackground,
		appearance.widgetText,
		appearance.accentColor,
	);
	const popup = resolve(
		widget,
		appearance.popupBackground,
		appearance.popupText,
		appearance.accentColor,
	);
	return { widget, popup };
};

/** A chosen pair the storefront will not correct, and that a shopper may not be able to read. */
export const isHardToRead = (background: string | null, text: string | null): boolean =>
	background !== null && text !== null && contrast(background, text) < MIN_TEXT_CONTRAST;
